import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, FileText, Brain, Loader2, CheckCircle2,
  AlertTriangle, Eye, Trash2, ChevronRight, RefreshCw,
  Check, X, Edit3
} from 'lucide-react';

const AI_STATUS_BADGES = {
  uploaded: { label: 'Uploaded', color: 'bg-slate-100 text-slate-600' },
  queued: { label: 'Queued', color: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Processing...', color: 'bg-yellow-100 text-yellow-700' },
  review_required: { label: 'Review Required', color: 'bg-purple-100 text-purple-700' },
  approved_for_creation: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  items_created: { label: 'Items Created', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
};

export default function ConsultantUploads({ projectId, settings }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [reviewUpload, setReviewUpload] = useState(null);
  const [extractedLines, setExtractedLines] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [packages, setPackages] = useState([]);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    package_id: '', consultant_company: '', consultant_name: '',
    discipline: '', inspection_date: '', round_no: '1',
    upload_type: 'live_defects', notes: '',
  });

  const loadUploads = useCallback(async () => {
    try {
      const res = await api.closeout.listUploads(projectId);
      setUploads(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadUploads(); }, [loadUploads]);
  useEffect(() => {
    api.closeout.listPackages(projectId).then(r => setPackages(r.data)).catch(() => {});
  }, [projectId]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }

    try {
      const params = { ...form };
      if (!params.package_id) delete params.package_id;
      await api.closeout.createUpload(projectId, file, params);
      toast.success('File uploaded successfully');
      setShowUpload(false);
      fileRef.current.value = '';
      loadUploads();
    } catch { toast.error('Upload failed'); }
  };

  const handleExtract = async (uploadId) => {
    setExtracting(true);
    try {
      const res = await api.closeout.triggerExtraction(uploadId);
      toast.success(`AI detected ${res.data.items_detected} items`);
      loadUploads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally { setExtracting(false); }
  };

  const handleReview = async (uploadId) => {
    try {
      const res = await api.closeout.getExtractedLines(uploadId);
      setExtractedLines(res.data);
      setReviewUpload(uploadId);
    } catch { toast.error('Failed to load extracted items'); }
  };

  const handleLineAction = async (lineId, action) => {
    try {
      await api.closeout.updateExtractedLine(lineId, { review_status: action });
      setExtractedLines(prev => prev.map(l => l.id === lineId ? { ...l, review_status: action } : l));
    } catch { toast.error('Update failed'); }
  };

  const handleApproveAll = async () => {
    setApproving(true);
    try {
      const res = await api.closeout.approveExtractedLines(reviewUpload);
      toast.success(`${res.data.count} defect items created`);
      setReviewUpload(null);
      setExtractedLines([]);
      loadUploads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally { setApproving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  // Review screen
  if (reviewUpload) {
    const activeLines = extractedLines.filter(l => l.review_status !== 'deleted');
    return (
      <div className="space-y-4" data-testid="ai-review-screen">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setReviewUpload(null); setExtractedLines([]); }}>
            <ChevronRight className="h-4 w-4 rotate-180" /> Back
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-barlow font-bold uppercase">AI Extraction Review</h2>
            <p className="text-sm text-slate-500">{activeLines.length} items to review</p>
          </div>
          <Button
            className="bg-green-600 hover:bg-green-700 gap-1"
            onClick={handleApproveAll}
            disabled={approving || activeLines.length === 0}
            data-testid="approve-all-btn"
          >
            {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Approve & Create Items ({activeLines.length})
          </Button>
        </div>

        {/* Review Table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">#</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Description</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Area</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Trade</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Severity</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Confidence</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {extractedLines.map((line, idx) => (
                <tr
                  key={line.id}
                  className={`border-b hover:bg-slate-50 ${line.review_status === 'deleted' ? 'opacity-30 line-through' : ''}`}
                  data-testid={`extracted-line-${line.id}`}
                >
                  <td className="px-3 py-2 text-slate-400">{line.line_no || idx + 1}</td>
                  <td className="px-3 py-2 max-w-[300px]">
                    <p className="text-slate-800 truncate">{line.description || line.raw_text}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{line.suggested_area}{line.suggested_room ? ` / ${line.suggested_room}` : ''}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px]">{line.suggested_trade}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[10px] ${
                      line.suggested_severity === 'blocker' ? 'bg-red-100 text-red-700' :
                      line.suggested_severity === 'major' ? 'bg-orange-100 text-orange-700' :
                      line.suggested_severity === 'minor' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{line.suggested_severity}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <div className={`h-2 w-12 rounded-full bg-slate-200 overflow-hidden`}>
                        <div
                          className={`h-full rounded-full ${line.confidence_score >= 0.8 ? 'bg-green-500' : line.confidence_score >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${(line.confidence_score || 0) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400">{Math.round((line.confidence_score || 0) * 100)}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {line.review_status !== 'deleted' ? (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-600" onClick={() => handleLineAction(line.id, 'approved')}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleLineAction(line.id, 'deleted')}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => handleLineAction(line.id, 'pending')}>Restore</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="consultant-uploads">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Consultant Uploads
          <span className="ml-2 text-sm font-normal text-slate-400">({uploads.length})</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowUpload(true)} data-testid="new-upload-btn">
          <Upload className="h-4 w-4" /> New Upload
        </Button>
      </div>

      {uploads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Upload className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">No consultant uploads yet</p>
            <p className="text-sm text-slate-400 mt-1">Upload a snag list to begin AI extraction</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {uploads.map(u => {
            const statusInfo = AI_STATUS_BADGES[u.ai_status] || AI_STATUS_BADGES.uploaded;
            return (
              <Card key={u.id} className="hover:shadow-sm transition-shadow" data-testid={`upload-${u.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{u.original_filename}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                      {u.consultant_company && <span>{u.consultant_company}</span>}
                      {u.discipline && <span>{u.discipline}</span>}
                      {u.inspection_date && <span>{u.inspection_date}</span>}
                      <span>Round {u.round_no}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {u.total_items_detected > 0 && (
                      <span className="text-xs text-slate-500">{u.total_items_detected} items</span>
                    )}
                    <Badge className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</Badge>
                    {u.ai_status === 'uploaded' && (
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => handleExtract(u.id)} disabled={extracting} data-testid={`extract-btn-${u.id}`}>
                        {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                        AI Extract
                      </Button>
                    )}
                    {u.ai_status === 'review_required' && (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 gap-1 h-7" onClick={() => handleReview(u.id)} data-testid={`review-btn-${u.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Review
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">Upload Consultant Snag List</DialogTitle>
          <DialogDescription>Upload a PDF, Word, or Excel snag list for AI extraction</DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">File *</Label>
              <Input type="file" ref={fileRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg" data-testid="upload-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Package</Label>
                <Select value={form.package_id || "none"} onValueChange={v => setForm(f => ({...f, package_id: v === "none" ? "" : v}))}>
                  <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {packages.map(p => <SelectItem key={p.id} value={p.id}>{p.package_code} - {p.package_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Upload Type</Label>
                <Select value={form.upload_type} onValueChange={v => setForm(f => ({...f, upload_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live_defects">Live Defects List</SelectItem>
                    <SelectItem value="completion_inspection">Completion Inspection</SelectItem>
                    <SelectItem value="reinspection">Reinspection List</SelectItem>
                    <SelectItem value="final_list">Final List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Consultant Company</Label>
                <Input value={form.consultant_company} onChange={e => setForm(f => ({...f, consultant_company: e.target.value}))} placeholder="e.g. Smith Architects" />
              </div>
              <div>
                <Label className="text-xs">Consultant Name</Label>
                <Input value={form.consultant_name} onChange={e => setForm(f => ({...f, consultant_name: e.target.value}))} placeholder="e.g. John Smith" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Discipline</Label>
                <Input value={form.discipline} onChange={e => setForm(f => ({...f, discipline: e.target.value}))} placeholder="e.g. Architecture" />
              </div>
              <div>
                <Label className="text-xs">Inspection Date</Label>
                <Input type="date" value={form.inspection_date} onChange={e => setForm(f => ({...f, inspection_date: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs">Round #</Label>
                <Input type="number" value={form.round_no} onChange={e => setForm(f => ({...f, round_no: e.target.value}))} min="1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Optional notes about this upload" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleUpload} data-testid="submit-upload-btn">
              <Upload className="h-4 w-4 mr-1" /> Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
